import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { CustomToastrService } from '../../shared/ngx-toastr/custom-toastr.service';
import { ERROR } from '../../shared/constants';

// ---------- Actions ----------

export class SetSurveyGridItems {
  static readonly type = '[SurveyGrid] Set Items';
  constructor(public items: any[]) {}
}

export class DeleteSurvey {
  static readonly type = '[SurveyGrid] Delete Survey';
  constructor(
    public surveyId: string,
    public primaryCompanyId: string,
  ) {}
}

export class DeleteSurveySuccess {
  static readonly type = '[SurveyGrid] Delete Survey Success';
  constructor(public surveyId: string) {}
}

export class RenameSurvey {
  static readonly type = '[SurveyGrid] Rename Survey';
  constructor(
    public surveyId: string,
    public primaryCompanyId: string,
    public newName: string,
  ) {}
}

export class RenameSurveySuccess {
  static readonly type = '[SurveyGrid] Rename Survey Success';
  constructor(
    public surveyId: string,
    public newName: string,
  ) {}
}

export class SurveyActionFailed {
  static readonly type = '[SurveyGrid] Survey Action Failed';
  constructor(public errorMessage: string) {}
}

// ---------- State Model ----------

export interface SurveyGridStateModel {
  items: any[];
  loading: boolean;
}

// ---------- NGXS State ----------

@State<SurveyGridStateModel>({
  name: 'surveyGrid',
  defaults: {
    items: [],
    loading: false,
  },
})
@Injectable()
export class SurveyGridState {
  constructor(private toastr: CustomToastrService) {}

  // ---------- Selectors ----------

  @Selector()
  static getItems(state: SurveyGridStateModel): any[] {
    return state.items;
  }

  @Selector()
  static getLoading(state: SurveyGridStateModel): boolean {
    return state.loading;
  }

  // ---------- Action Handlers ----------

  @Action(SetSurveyGridItems)
  setSurveyGridItems(
    ctx: StateContext<SurveyGridStateModel>,
    action: SetSurveyGridItems,
  ): void {
    ctx.patchState({ items: action.items });
  }

  @Action(DeleteSurvey)
  deleteSurvey(ctx: StateContext<SurveyGridStateModel>): void {
    ctx.patchState({ loading: true });
  }

  @Action(DeleteSurveySuccess)
  deleteSurveySuccess(
    ctx: StateContext<SurveyGridStateModel>,
    action: DeleteSurveySuccess,
  ): void {
    const state = ctx.getState();
    const updatedItems = state.items.filter(
      (survey) => survey.id !== action.surveyId,
    );
    ctx.patchState({ items: updatedItems, loading: false });
  }

  @Action(RenameSurvey)
  renameSurvey(ctx: StateContext<SurveyGridStateModel>): void {
    ctx.patchState({ loading: true });
  }

  @Action(RenameSurveySuccess)
  renameSurveySuccess(
    ctx: StateContext<SurveyGridStateModel>,
    action: RenameSurveySuccess,
  ): void {
    const state = ctx.getState();
    const updatedItems = state.items.map((survey) =>
      survey.id === action.surveyId
        ? { ...survey, surveyName: action.newName }
        : survey,
    );
    ctx.patchState({ items: updatedItems, loading: false });
  }

  @Action(SurveyActionFailed)
  surveyActionFailed(
    ctx: StateContext<SurveyGridStateModel>,
    action: SurveyActionFailed,
  ): void {
    ctx.patchState({ loading: false });
    this.toastr.showError(action.errorMessage, ERROR);
  }
}
